<script lang="ts">
	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
	import heroShot from "@assets/images/case-study/sladkadilna-web.png?webp&w=1280&imagetools";
	import { Background } from "@components/Background.js";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import Media from "@components/common/Media.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import bracketsIcon from "@core/assets/icons/brackets.svg?raw";
	import cardIcon from "@core/assets/icons/card.svg?raw";
	import codeIcon from "@core/assets/icons/code.svg?raw";
	import dataObjectIcon from "@core/assets/icons/data-object.svg?raw";
	import databaseIcon from "@core/assets/icons/database.svg?raw";
	import deliveryIcon from "@core/assets/icons/delivery-speed.svg?raw";
	import devicesIcon from "@core/assets/icons/devices.svg?raw";
	import envelopeIcon from "@core/assets/icons/envelope.svg?raw";
	import factoryIcon from "@core/assets/icons/factory.svg?raw";
	import gitIcon from "@core/assets/icons/git.svg?raw";
	import graphIcon from "@core/assets/icons/graph.svg?raw";
	import headphonesIcon from "@core/assets/icons/headphones.svg?raw";
	import integrationIcon from "@core/assets/icons/integration.svg?raw";
	import phoneIcon from "@core/assets/icons/phone.svg?raw";
	import quoteIcon from "@core/assets/icons/quote.svg?raw";
	import retailIcon from "@core/assets/icons/retail.svg?raw";
	import cartIcon from "@core/assets/icons/shopping-cart.svg?raw";
	import switchAccessIcon from "@core/assets/icons/switch-access.svg?raw";
	import ucetnictviIcon from "@core/assets/icons/ucetnictvi.svg?raw";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getDevelopmentPath } from "@core/schema/paths/getDevelopmentPath.js";
	import { getDigitalizationAndAutomationPath } from "@core/schema/paths/getDigitalizationAndAutomationPath.js";
	import { getEcommercePath } from "@core/schema/paths/getEcommercePath.js";
	import { getGastroPath } from "@core/schema/paths/getGastroPath.js";
	import { getIntegrationsPath } from "@core/schema/paths/getIntegrationsPath.js";
	import { getProductionPath } from "@core/schema/paths/getProductionPath.js";
	import { getWebAppsPath } from "@core/schema/paths/getWebAppsPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import type { CaseStudySladkaDilnaProps } from "./CaseStudySladkaDilnaProps.js";

	const _: CaseStudySladkaDilnaProps = $props();
	const { tenant } = getGlobalTemplateProps();
	const contacts = getBookMeetingPeople(tenant);
	const martin = contacts[BookMeetingPerson.Kucera];

	const solutionParts = [
		{
			label: "Pro zákazníky",
			name: "E-shop",
			desc: "Online prodej dortů, zákusků a minidezertů s objednávkou do druhého dne.",
			icon: cartIcon,
			featured: false,
		},
		{
			label: "Pro celý provoz",
			name: "Interní systém",
			desc: "Nejkomplexnější část — řídí objednávky, výrobu, sklad, pobočky i účetnictví.",
			icon: databaseIcon,
			featured: true,
		},
		{
			label: "Pro partnery",
			name: "B2B portál",
			desc: "Komunikace a spolupráce s partnerskými kavárnami a cukrárnami.",
			icon: integrationIcon,
			featured: false,
		},
	];

	const systemFeatures = [
		{ icon: cartIcon, label: "Správa objednávek" },
		{ icon: cardIcon, label: "Tisk štítků na krabičky" },
		{ icon: databaseIcon, label: "Správa skladových zásob" },
		{ icon: factoryIcon, label: "Modul pro výrobnu" },
		{ icon: retailIcon, label: "Modul pro provozovny" },
		{ icon: graphIcon, label: "Přehledy pro management" },
		{ icon: ucetnictviIcon, label: "Automatizované účetnictví a platby" },
		{ icon: cardIcon, label: "Správa voucherů" },
		{ icon: headphonesIcon, label: "Správa speciálních poptávek" },
		{ icon: dataObjectIcon, label: "Audit úprav dat v systému" },
		{ icon: switchAccessIcon, label: "Správa uživatelů a oprávnění" },
		{ icon: deliveryIcon, label: "Modul pro řidiče" },
	];

	const technologies = [
		"AWS",
		"DynamoDB",
		"AppSync",
		"Svelte",
		"Tailwind",
		"GraphQL",
		"GoPay",
		"Fakturoid",
		"Google Analytics 4",
		"Sentry",
		"Smartlook",
	];

	const timeline = [
		{
			date: "19. 3.",
			label: "Úvodní schůzka",
			desc: "Společně jsme definovali zadání i rozpočet a poznali provoz cukrárny do detailu.",
		},
		{
			date: "22. 3.",
			label: "Návrh řešení",
			desc: "Tři dny po schůzce dostal klient od Fastest hotový návrh řešení ke schválení.",
		},
		{
			date: "03–05",
			label: "Týdenní sprinty",
			desc: "Agilní vývoj na cloudu AWS, klient u každého detailu. Nejdřív funkce, pak grafika.",
		},
		{
			date: "5. 6.",
			label: "Spuštění první verze",
			desc: "První funkční verze byla hotová dřív než za 3 měsíce — e-shop, systém i B2B portál.",
		},
	];

	const bannerLinks = [
		{ label: "Vývoj webových aplikací", href: getWebAppsPath(tenant) },
		{ label: "Vývoj na míru", href: getDevelopmentPath(tenant) },
		{ label: "Integrace systémů", href: getIntegrationsPath(tenant) },
		{ label: "Digitalizace a automatizace", href: getDigitalizationAndAutomationPath(tenant) },
	];

	let showStickyCta = $state(false);
	$effect((): (() => void) => {
		const onScroll = (): void => {
			const max = document.documentElement.scrollHeight - window.innerHeight;
			showStickyCta = window.scrollY > 700 && window.scrollY < max - 700;
		};
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => {
			window.removeEventListener("scroll", onScroll);
		};
	});
</script>

<!-- Velká CTA karta: zelený icon box + green eyebrow + nadpis + popis + phlox tlačítko -->
{#snippet ctaBig(icon: string, eyebrow: string, heading: string, desc: string, btnText: string, href: string)}
	<div class="container-12">
		<div
			class="flex flex-col gap-8 rounded-[1.25rem] bg-white p-8 shadow-[0_8px_30px_rgba(33,32,32,0.06)] md:flex-row md:items-center md:gap-10 md:p-11"
		>
			<div class="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-[18px] bg-fstgreen">
				<Icon {icon} class="w-9 text-fstonyx" square />
			</div>
			<div class="flex-1">
				<p class="!mb-1 text-base font-medium text-fstgreen">{eyebrow}</p>
				<h3 class="!mb-2 !mt-0 text-md font-black text-fstonyx md:text-2md">{heading}</h3>
				<p class="!mb-0 text-base text-fstgrey-600">{desc}</p>
			</div>
			<Link variant={ButtonVariant.Phlox} class="flex shrink-0 items-center justify-center sm:!w-auto" {href}>
				{btnText}
				<Icon icon={arrowNext} class="ml-2 shrink-0 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</div>
{/snippet}

<!-- Inline CTA karta: zelený levý border + ikona + nadpis + popis + green text link -->
{#snippet ctaInline(icon: string, heading: string, desc: string, linkText: string, href: string)}
	<div class="container-12">
		<div
			class="flex flex-col gap-6 rounded-[1.25rem] border-l-4 border-fstgreen bg-white p-6 shadow-[0_8px_30px_rgba(33,32,32,0.04)] md:flex-row md:items-center md:gap-10 md:px-10 md:py-7"
		>
			<div class="flex h-12 w-12 shrink-0 items-center justify-center text-fstgreen">
				<Icon {icon} class="w-8" square />
			</div>
			<div class="flex-1">
				<h3 class="!mb-1 !mt-0 text-base font-bold text-fstonyx md:text-md">{heading}</h3>
				<p class="!mb-0 text-sm text-fstgrey-600 md:text-base">{desc}</p>
			</div>
			<a
				{href}
				class="flex shrink-0 items-center gap-2 font-medium text-fstgreen transition-colors hover:text-fstonyx md:whitespace-nowrap"
			>
				{linkText}
				<Icon icon={arrowNext} class="w-5 shrink-0"></Icon>
			</a>
		</div>
	</div>
{/snippet}

<Layout>
	<!-- Hero -->
	<Section hero background={Background.LightGradient} noPadding="top">
		<div class="container-12 grid grid-cols-1 items-center gap-16 pt-14 lg:grid-cols-2 lg:gap-20">
			<div>
				<span class="mb-6 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgrey-600">
					Případová studie · Sladká dílna
				</span>
				<h1 class="!mb-6">
					E-shop a interní systém na míru <br class="hidden lg:block" />
					<span class="text-fstgreen">do 3&nbsp;měsíců</span>
				</h1>
				<p class="mb-8 max-w-xl text-md leading-[1.5em] text-fstgrey-600 lg:text-2md">
					<Formatted
						text="Jak jsme rodinné cukrárně sp[[Sladká dílna]] digitalizovali objednávky, propojili výrobu i prodej „pod jednu střechu&quot; a po spuštění zvýšili počet objednávek o polovinu."
					/>
				</p>
				<div class="mb-10 flex flex-col gap-4 sm:flex-row">
					<Link
						variant={ButtonVariant.Phlox}
						class="flex items-center justify-center sm:!w-auto"
						href={getConnectWithFastestPath(tenant)}
					>
						Chci podobné řešení
						<Icon icon={arrowNext} class="ml-2 shrink-0 text-fstgrey-900"></Icon>
					</Link>
					<Link
						variant={ButtonVariant.GreenOutlineLightBg}
						class="flex items-center justify-center sm:!w-auto"
						href="#pribeh"
					>
						Přečíst příběh
					</Link>
				</div>
				<ul class="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-x-6">
					{#each ["Celý provoz z jedné aplikace", "Konec ručního přepisování objednávek", "Prodej i výroba propojené naživo"] as point (point)}
						<li
							class="flex items-center gap-2 text-sm text-fstgrey-600 before:h-1.5 before:w-1.5 before:shrink-0 before:rounded-full before:bg-fstgreen before:content-['']"
						>
							{point}
						</li>
					{/each}
				</ul>
			</div>

			<!-- Browser mockup s plovoucími pilíři – skryt na mobilu, viditelný od lg -->
			<div class="relative mx-auto hidden w-full max-w-xl lg:block">
				<div class="overflow-hidden rounded-[1.25rem] bg-white shadow-[0_20px_60px_rgba(33,32,32,0.12)]">
					<div class="flex items-center gap-2 border-b border-fstlight-200 px-4 py-3">
						<span class="h-3 w-3 rounded-full bg-fstlight-300"></span>
						<span class="h-3 w-3 rounded-full bg-fstlight-300"></span>
						<span class="h-3 w-3 rounded-full bg-fstlight-300"></span>
						<span class="ml-3 flex-1 rounded-full bg-fstlight-100 px-4 py-1 text-xs text-fstgrey-300">
							sladkadilna.cz
						</span>
					</div>
					<img
						src={heroShot}
						alt="Web a e-shop Sladké dílny"
						class="block aspect-[1440/900] w-full object-cover object-top"
						width="1280"
						height="800"
					/>
				</div>
				<span
					class="absolute -right-3 top-8 flex items-center gap-2 rounded-full bg-fstgreen px-4 py-2 text-sm font-medium text-fstonyx shadow-lg max-lg:hidden"
				>
					<span class="flex h-5 w-5 items-center justify-center rounded-full bg-fstonyx text-xs text-fstgreen">+</span>
					Celý provoz z jedné aplikace
				</span>
				<span
					class="absolute -left-4 top-2/3 flex items-center gap-2 rounded-full bg-fstgreen px-4 py-2 text-sm font-medium text-fstonyx shadow-lg max-lg:hidden"
				>
					<span class="flex h-5 w-5 items-center justify-center rounded-full bg-fstonyx text-xs text-fstgreen">+</span>
					Konec ručního přepisování objednávek
				</span>
				<span
					class="absolute -right-3 bottom-8 flex items-center gap-2 rounded-full bg-fstgreen px-4 py-2 text-sm font-medium text-fstonyx shadow-lg max-lg:hidden"
				>
					<span class="flex h-5 w-5 items-center justify-center rounded-full bg-fstonyx text-xs text-fstgreen">+</span>
					Prodej i výroba propojené naživo
				</span>
			</div>
		</div>
	</Section>

	<!-- Statistiky -->
	<Section background={Background.Onyx}>
		<div class="container-12 grid grid-cols-2 gap-8 text-white lg:grid-cols-4 lg:gap-12">
			{#each [{ n: "+50 %", d: "nárůst objednávek v prvním měsíci po spuštění" }, { n: "3 měsíce", d: "od zadání ke spuštění první funkční verze" }, { n: "3 části", d: "E-shop, interní systém a B2B portál. Vše spolu komunikuje." }, { n: "100 %", d: "řešení podle provozu klienta bez kompromisů" }] as stat (stat.d)}
				<div>
					<p class="mb-2 font-loos-wide text-[1.875rem] font-black leading-none text-fstgreen lg:text-[2.25rem]">
						{stat.n}
					</p>
					<p class="text-sm leading-snug text-fstgrey-300">{stat.d}</p>
				</div>
			{/each}
		</div>
	</Section>

	<!-- O klientovi -->
	<Section id="pribeh" background={Background.White} gridChildren="container-12">
		<div class="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
			<div>
				<span class="mb-4 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgrey-600">
					O klientovi
				</span>
				<h2 class="!mb-6">Rodinná cukrárna, která chtěla růst&nbsp;— bez ručního chaosu</h2>
				<p class="max-w-xl text-md leading-[1.5em]">
					<Formatted
						text="[https://www.sladkadilna.cz][[Sladká dílna]] je rodinná cukrárna z Prahy, která se specializuje na výrobu sladkých dobrot z prvotřídních surovin. Kromě klasických dortů a zákusků nabízí i minidezerty a svatební dorty, většinu objednávek zvládne připravit do druhého dne. V hlavním městě provozuje dvě prodejny."
					/>
				</p>
				<p class="max-w-xl text-md italic leading-[1.5em] text-fstgrey-500">
					<Formatted
						text="„Potřebujeme nový e-shop a interní systém, ale žádné z vyzkoušených řešení nám nevyhovuje,&quot; prozradili nám v březnu 2024 majitelé. A tak jsme přijali výzvu postavit v krátkém čase řešení na míru."
					/>
				</p>
			</div>

			<div class="rounded-[1.25rem] border-4 border-fstlight-100 bg-fstlight-100 p-6 md:p-8">
				<span class="mb-6 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgrey-300">
					Klíčové informace o zakázce
				</span>
				<dl class="space-y-4">
					{#each [{ t: "Klient", d: "Sladká dílna — rodinná cukrárna, Praha (2 provozovny)" }, { t: "Typ projektu", d: "E-shop s interním systémem a B2B portálem na míru" }, { t: "Období realizace", d: "03/2024 – 06/2024 · spolupráce pokračuje doposud" }, { t: "Technologie", d: "AWS, Svelte, DynamoDB, AppSync, GraphQL, Tailwind" }] as row (row.t)}
						<div class="border-b border-fstlight-300 pb-4 last:border-0 last:pb-0">
							<dt class="mb-1 text-sm text-fstgrey-300">{row.t}</dt>
							<dd class="text-base font-medium text-fstonyx">{row.d}</dd>
						</div>
					{/each}
				</dl>
				<div class="mt-6 flex flex-wrap gap-2">
					{#each [{ label: "E-commerce", href: getEcommercePath(tenant) }, { label: "Gastro", href: getGastroPath(tenant) }, { label: "Výroba", href: getProductionPath(tenant) }, { label: "Digitalizace", href: getDigitalizationAndAutomationPath(tenant) }] as tag (tag.label)}
						<a
							href={tag.href}
							class="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-fstgrey-300 transition-colors hover:text-fstgreen"
							>{tag.label}</a
						>
					{/each}
				</div>
			</div>
		</div>
	</Section>

	<!-- Výzva + timeline + citáty -->
	<Section background={Background.Light} gridChildren="container-12">
		<span class="mb-4 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgrey-600">
			Výzva
		</span>
		<h2 class="!mb-6"><Formatted text="„Zvládnete to do 3 měsíců?&quot;" /></h2>
		<p class="max-w-3xl text-md leading-[1.5em]">
			Zadání mělo jeden háček: klient potřeboval první funkční verzi nejpozději v červnu — tři měsíce od poptávky. Cíl
			byl jasný: e-shop a interní systém na míru pro správu objednávek, tisk štítků na krabičky i přehled o skladu.
			Konec chaotického zapisování objednávek na papírky.
		</p>

		<div class="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
			{#each timeline as milestone (milestone.date)}
				<div class="relative">
					<div class="mb-5 flex items-center">
						<span class="h-4 w-4 shrink-0 rounded-full bg-fstgreen"></span>
						<span class="ml-3 h-[2px] flex-1 bg-fstlight-300"></span>
					</div>
					<span class="mb-1 block font-loos-wide text-md font-black text-fstgreen">{milestone.date}</span>
					<span class="mb-2 block text-base font-bold text-fstonyx">{milestone.label}</span>
					<p class="!mb-0 text-sm leading-snug text-fstgrey-600">{milestone.desc}</p>
				</div>
			{/each}
		</div>

		<div class="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
			<blockquote class="border-l-4 border-fstgreen pl-6">
				<p class="!mb-4 text-md font-bold leading-snug text-fstonyx">
					<Formatted
						text="„Průběh spolupráce byl hladký, rychlý a často i zábavný. Martin Kučera nás procesem trpělivě provedl a vše nám vysvětlil.&quot;"
					/>
				</p>
				<footer class="text-sm text-fstgrey-500">Andrej Malon, majitel Sladké dílny</footer>
			</blockquote>
			<blockquote class="border-l-4 border-fstgreen pl-6">
				<p class="!mb-4 text-md font-bold leading-snug text-fstonyx">
					<Formatted
						text="„Mohli jsme se v průběhu zakázky podílet na každém detailu, což je pro nás jako pro podnik se specifickými požadavky důležité. Nutno podotknout, že tým Fastest se rychle nacítil na problematiku našeho provozu, a tak jsme vše zvládli online. Ocenit musíme i analytické myšlení, které nás doslova posadilo a utvrdilo, že jsme ve správných rukou.&quot;"
					/>
				</p>
				<footer class="text-sm text-fstgrey-500">Sandra Martínková, majitelka Sladké dílny</footer>
			</blockquote>
		</div>
	</Section>

	<!-- CTA 2 -->
	<Section background={Background.Light} noPadding="top">
		{@render ctaInline(
			deliveryIcon,
			"Potřebujete rychle ověřit, jestli vlastní řešení dává smysl?",
			"Začínáme nejmenší funkční verzí, kterou nasadíme a rozvíjíme podle reálného používání.",
			"Ověřte, co zvládneme za 3 měsíce",
			getConnectWithFastestPath(tenant),
		)}
	</Section>

	<!-- Řešení -->
	<Section background={Background.White} gridChildren="container-12">
		<span class="mb-4 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgrey-600">
			Řešení
		</span>
		<h2 class="!mb-6">Celý provoz „pod jednou střechou"</h2>
		<p class="max-w-3xl text-md leading-[1.5em]">
			Zakázka stála na třech propojených částech. Společně zajišťují plynulý proces prodeje i výroby — online a bez
			ručního přepisování.
		</p>

		<div class="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
			{#each solutionParts as part (part.name)}
				<div class={["rounded-[1.25rem] p-8", part.featured ? "bg-fstonyx" : "border-4 border-fstlight-100 bg-white"]}>
					<div
						class={[
							"mb-6 flex h-14 w-14 items-center justify-center rounded-[14px]",
							part.featured ? "bg-fstgreen text-fstonyx" : "bg-fstlight-100 text-fstgreen",
						]}
					>
						<Icon icon={part.icon} class="w-7" square />
					</div>
					<span
						class={[
							"mb-2 block font-loos-normal text-xs font-medium uppercase tracking-[0.25em]",
							part.featured ? "text-fstgreen" : "text-fstgrey-300",
						]}
					>
						{part.label}
					</span>
					<h3 class={["!mb-2 !mt-0 text-md font-black", part.featured ? "text-white" : "text-fstonyx"]}>
						{part.name}
					</h3>
					<p class={["!mb-0 text-sm leading-snug", part.featured ? "text-fstgrey-300" : "text-fstgrey-600"]}>
						{part.desc}
					</p>
				</div>
			{/each}
		</div>

		<div class="mt-16">
			<h3 class="!mb-2 text-base font-bold text-fstonyx md:text-md">Interní systém: celý provoz z jedné aplikace</h3>
			<p class="mb-8 text-sm text-fstgrey-300">obsluha · manažeři · vedoucí cukrářka · výroba · řidiči · marketing</p>
			<div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
				{#each systemFeatures as feature (feature.label)}
					<div class="flex items-center gap-4 rounded-[14px] bg-fstlight-100 px-5 py-4">
						<Icon icon={feature.icon} class="w-6 shrink-0 text-fstgreen" square />
						<span class="text-base font-medium text-fstonyx">{feature.label}</span>
					</div>
				{/each}
			</div>
		</div>

		<div class="mt-12 overflow-hidden rounded-[1.25rem] bg-fstlight-100">
			<div class="flex items-center gap-2 border-b border-fstlight-200 px-4 py-3">
				<span class="h-3 w-3 rounded-full bg-fstlight-300"></span>
				<span class="h-3 w-3 rounded-full bg-fstlight-300"></span>
				<span class="h-3 w-3 rounded-full bg-fstlight-300"></span>
				<span class="ml-3 flex-1 rounded-full bg-white px-4 py-1 text-xs text-fstgrey-300">sladkadilna.cz</span>
			</div>
			<img src={heroShot} alt="Web a e-shop Sladké dílny" class="block w-full" width="1280" height="800" />
		</div>
	</Section>

	<!-- CTA 3 -->
	<Section background={Background.Light}>
		{@render ctaInline(
			codeIcon,
			"Máte provoz, který už nestačí řídit přes tabulky a ruční kontroly?",
			"Navrhneme systém, který propojí objednávky, sklad, výrobu, pobočky, účetnictví i reporting.",
			"Jak vyvíjíme webové aplikace na míru",
			getWebAppsPath(tenant),
		)}
	</Section>

	<!-- Tmavá citace -->
	<Section background={Background.Onyx} centered gridHeadline="container-10 xl:container-8">
		<div class="container-10 mx-auto text-center xl:container-8">
			<Icon icon={quoteIcon} class="mx-auto mb-6 h-12 w-12 text-fstgreen" square />
			<p class="mx-auto max-w-3xl text-lg font-black leading-tight text-white md:text-xl lg:text-2xl">
				Interní systém nám teď ohromně šetří čas. Už nemusíme každou objednávku zapisovat ručně a neděláme chyby ve
				jménech nebo telefonních číslech. <span class="text-fstgreen">Vše za nás řeší počítač.</span>
			</p>
			<div class="mt-8">
				<p class="text-base font-bold text-white">Andrej a Sandra</p>
				<p class="text-sm text-fstgrey-300">majitelé Sladké dílny</p>
			</div>
		</div>
	</Section>

	<!-- CTA 1 přesunuto sem z pozice po stats -->
	<Section background={Background.Light}>
		{@render ctaBig(
			bracketsIcon,
			"Řešíte podobný provoz?",
			"Postavíme e-shop, interní systém nebo B2B portál podle toho, jak vaše firma opravdu funguje.",
			"Nejdřív se podíváme na objednávky, sklad, výrobu, pobočky a ruční práci, která dnes brzdí provoz.",
			"Chci podobné řešení",
			getConnectWithFastestPath(tenant),
		)}
	</Section>

	<!-- Výsledek -->
	<Section background={Background.White} gridChildren="container-12">
		<div class="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center lg:gap-16">
			<div>
				<span class="mb-4 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgrey-600">
					Výsledek
				</span>
				<p class="!mb-3 font-loos-wide text-[4rem] font-black leading-none text-fstgreen md:text-[5rem] lg:text-[6rem]">
					+50 %
				</p>
				<p class="text-lg font-medium text-fstonyx">více objednávek po spuštění</p>
			</div>
			<div>
				<h2 class="!mb-6">Nárůst byl tak velký, že jsme přidali „záchrannou brzdu"</h2>
				<p class="text-md leading-[1.5em]">
					E-shop i interní systém se podařilo dokončit podle představ klienta a v požadovaném termínu do 3 měsíců. Po
					spuštění nového e-shopu počet objednávek vzrostl o 50 %.
				</p>
				<p class="text-md leading-[1.5em]">
					<Formatted
						text="Nárůst byl dokonce takový, že nás majitelé poprosili o funkci sp[[„záchranná brzda&quot;]], která zastaví příjem objednávek při velkém vytížení cukrárny. Na vývoji spolupracujeme dál — do podzimu 2024 jsme systém doplnili o další funkce."
					/>
				</p>
				<ul class="mt-6 space-y-3">
					{#each ["Dodáno v termínu do 3 měsíců", "Spolupráce pokračuje doposud"] as item (item)}
						<li class="flex items-center gap-3 font-medium text-fstonyx">
							<span
								class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-fstgreen text-xs text-white"
							>
								✓
							</span>
							{item}
						</li>
					{/each}
				</ul>
			</div>
		</div>
	</Section>

	<!-- CTA 5 -->
	<Section background={Background.Light}>
		{@render ctaBig(
			gitIcon,
			"Chcete systém, který zvládne růst firmy?",
			"Když se objednávky zvednou, provoz to musí ustát.",
			"Pomůžeme vám navrhnout e-shop, interní systém nebo integrace tak, aby růst neznamenal víc ruční práce, chyb a chaosu v objednávkách.",
			"Chci vlastní řešení",
			getConnectWithFastestPath(tenant),
		)}
	</Section>

	<!-- Technologie -->
	<Section background={Background.Light} centered gridHeadline="container-10" noPadding="top">
		{#snippet headline()}
			<span class="mb-4 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgrey-600">
				Technologie
			</span>
			Postaveno na moderním cloud-native stacku
		{/snippet}
		<div class="container-12 flex flex-wrap justify-center gap-3">
			{#each technologies as tech (tech)}
				<span
					class="flex items-center gap-2 rounded-full border-2 border-fstlight-300 bg-white px-5 py-2.5 text-base font-medium text-fstonyx before:h-1.5 before:w-1.5 before:shrink-0 before:rounded-full before:bg-fstgreen before:content-['']"
				>
					{tech}
				</span>
			{/each}
		</div>
	</Section>

	<!-- Spodní banner -->
	<Section background={Background.Onyx} gridChildren="container-12">
		<div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-[3fr_2fr] lg:gap-20">
			<div>
				<span class="mb-6 block font-loos-normal text-xs font-medium uppercase tracking-[0.34em] text-fstgreen">
					Pojďme to probrat
				</span>
				<h2 class="!mb-6 text-white">Máte e-shop, B2B prodej nebo provoz, který už přerostl hotové řešení?</h2>
				<p class="mb-8 text-md leading-[1.5em] text-fstgrey-300">
					Podíváme se na vaše objednávky, sklad, výrobu, pobočky, účetnictví i reporting. Řekneme vám na rovinu, co
					stačí propojit, co automatizovat a kde už dává smysl vlastní systém.
				</p>
				<ul class="grid grid-cols-1 gap-3 sm:grid-cols-2">
					{#each bannerLinks as link (link.label)}
						<li>
							<a
								href={link.href}
								class="flex items-center gap-2 text-base text-fstgrey-300 transition-colors hover:text-fstgreen"
							>
								<span class="text-fstgreen">→</span>
								{link.label}
							</a>
						</li>
					{/each}
				</ul>
			</div>

			<div
				class="w-full rounded-t-[1.25rem] rounded-b-[1.5rem] bg-fstgrey-800 px-[2.125rem] pt-[2.125rem] pb-[2.25rem] flex flex-col gap-5"
			>
				<div class="flex items-center gap-5">
					{#if martin.image}
						<div class="shrink-0 rounded-full border-4 border-fstgreen p-[3px]">
							<Media
								source={martin.image}
								alt={martin.name}
								width={96}
								height={96}
								class="h-24 w-24 rounded-full object-cover"
							/>
						</div>
					{/if}
					<div>
						<p class="text-[1.5rem] font-bold leading-tight text-white">{martin.name}</p>
						<p class="mt-1 text-sm font-medium text-fstgreen">CEO · Solution Owner</p>
					</div>
				</div>
				<p class="text-sm leading-[1.375rem] text-fstlight-300">
					Přebírá odpovědnost za pochopení vašeho byznysu, návrh řešení i dodání funkčního výsledku.
				</p>
				<div class="flex flex-col gap-3">
					{#if martin.phone}
						<a
							href="tel:{martin.phone}"
							class="flex items-center gap-4 text-white transition-colors hover:text-fstgreen"
						>
							<span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-fstgrey-900">
								<Icon icon={phoneIcon} class="w-5 text-fstgreen" square />
							</span>
							<span class="text-base font-medium">{martin.phone}</span>
						</a>
					{/if}
					{#if martin.email}
						<a
							href="mailto:{martin.email}"
							class="flex items-center gap-4 text-white transition-colors hover:text-fstgreen"
						>
							<span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-fstgrey-900">
								<Icon icon={envelopeIcon} class="w-5 text-fstgreen" square />
							</span>
							<span class="text-base font-medium">{martin.email}</span>
						</a>
					{/if}
				</div>
				<Link
					variant={ButtonVariant.Phlox}
					class="flex w-full items-center justify-center"
					href={getConnectWithFastestPath(tenant)}
				>
					Domluvit schůzku
					<Icon icon={arrowNext} class="ml-2 shrink-0 text-fstgrey-900"></Icon>
				</Link>
			</div>
		</div>
	</Section>

	<!-- Sticky floating CTA -->
	<div
		class={[
			"fixed bottom-6 left-1/2 z-40 -translate-x-1/2 transition-all duration-300 max-md:hidden",
			showStickyCta ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0",
		]}
	>
		<div class="flex items-center gap-4 rounded-full bg-fstonyx py-2 pl-6 pr-2 shadow-[0_8px_30px_rgba(33,32,32,0.25)]">
			<span class="flex items-center gap-2 text-sm font-medium text-white">
				<span class="h-2 w-2 rounded-full bg-fstgreen"></span>
				Řešíte podobný provoz?
			</span>
			<Link
				variant={ButtonVariant.Phlox}
				class="flex !w-auto items-center justify-center !py-2.5"
				href={getConnectWithFastestPath(tenant)}
			>
				Probrat můj projekt
				<Icon icon={arrowNext} class="ml-2 shrink-0 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</div>
</Layout>
